import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies, categories } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeCat, setActiveCat] = useState(slug || 'all');
  const sortParam = searchParams.get('sort') || 'default';

  useEffect(() => {
    setActiveCat(slug || 'all');
  }, [slug]);

  const filtered = useMemo(() => {
    let result = activeCat === 'all' ? [...movies] : movies.filter((m) => m.category === activeCat);

    if (sortParam === 'hot') {
      result = result.filter((m) => m.isHot).sort((a, b) => b.rating - a.rating);
    } else if (sortParam === 'new') {
      result = result.filter((m) => m.isNew).sort((a, b) => b.year - a.year);
    } else if (sortParam === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    }

    return result;
  }, [activeCat, sortParam]);

  const activeCategory = categories.find((c) => c.slug === activeCat);
  const pageTitle = activeCat === 'all' ? '全部影片' : activeCategory?.name || '影片列表';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `窝窝影院 - ${pageTitle}`,
    description: `窝窝影院${pageTitle}分类，高清播放在线不缓冲，超清画质无广告，海量免费电影电视剧在线观看。`,
    url: `https://wowocinema.com/category/${activeCat}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '窝窝影院',
      url: 'https://wowocinema.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: pageTitle },
            ]}
          />

          <h1 className="font-heading text-2xl md:text-3xl text-foreground-100 mb-2">{pageTitle}</h1>
          <p className="text-foreground-500 text-sm mb-6">
            窝窝影院提供高清播放、在线不缓冲的流畅观影体验，超清画质无广告
          </p>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}${sortParam !== 'default' ? `?sort=${sortParam}` : ''}`}
                className={`px-5 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-300 cursor-pointer animate-fade-in ${
                  activeCat === cat.slug
                    ? 'bg-primary-500 text-background-950 font-medium'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-foreground-500 text-sm whitespace-nowrap">排序：</span>
            {[
              { value: 'default', label: '默认' },
              { value: 'hot', label: '热门' },
              { value: 'new', label: '最新' },
              { value: 'rating', label: '高分' },
            ].map((s) => (
              <Link
                key={s.value}
                to={`/category/${activeCat}${s.value !== 'default' ? `?sort=${s.value}` : ''}`}
                className={`text-sm px-4 py-1.5 rounded-full whitespace-nowrap transition-all cursor-pointer ${
                  sortParam === s.value
                    ? 'bg-accent-500 text-foreground-50'
                    : 'bg-background-100 text-foreground-500 hover:text-foreground-300'
                }`}
              >
                {s.label}
              </Link>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-12"
              data-product-shop
            >
              {filtered.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 animate-fade-in">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-film-line text-foreground-600 text-4xl"></i>
              </div>
              <p className="text-foreground-500">暂无相关影片</p>
            </div>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}